import { motion } from 'framer-motion'
import { AreaChart, Area, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts'
import { useSimStore } from '../store/simStore'
import { useState, useEffect } from 'react'

function StatCard({ label, value, sub, colour = 'text-teal-400' }: {
  label: string; value: string; sub?: string; colour?: string
}) {
  return (
    <div className="bg-[#161b22]/80 backdrop-blur border border-[#30363d] rounded-lg p-3 text-center">
      <div className={`text-xl font-bold font-mono ${colour}`}>{value}</div>
      <div className="text-xs text-slate-400 mt-1">{label}</div>
      {sub && <div className="text-xs text-slate-500">{sub}</div>}
    </div>
  )
}

/** Mini arc gauge for SPI (0–1).
 *  Uses the top semicircle (sweep=0, counterclockwise) so the arc stays
 *  within the SVG viewBox: arc goes from (left,cy) up through (cx, cy-r)
 *  to (right,cy), occupying y=[cy-r, cy]. */
function SpiGauge({ spi }: { spi: number }) {
  const r = 28
  const cx = 36
  const cy = 38          // baseline; arc top at cy-r=10
  const strokeW = 6
  const circumference = Math.PI * r   // half-circle arc length
  const filled = Math.min(1, Math.max(0, spi)) * circumference
  const colour = spi >= 0.85 ? '#10b981' : spi >= 0.65 ? '#f59e0b' : '#f43f5e'

  // Endpoints sit on the horizontal diameter
  const startX = cx - r   // left  (8, cy)
  const endX   = cx + r   // right (64, cy)

  // sweep=0 → counterclockwise → top semicircle (arc climbs above cy)
  const arc = `M ${startX} ${cy} A ${r} ${r} 0 0 0 ${endX} ${cy}`

  return (
    <svg width={72} height={50} className="mx-auto">
      {/* Track (full arc) */}
      <path d={arc} fill="none" stroke="#21262d" strokeWidth={strokeW} strokeLinecap="round" />
      {/* Fill (partial arc based on SPI) */}
      <path
        d={arc}
        fill="none" stroke={colour} strokeWidth={strokeW} strokeLinecap="round"
        strokeDasharray={`${filled} ${circumference}`}
        style={{ transition: 'stroke-dasharray 0.6s ease, stroke 0.4s' }}
      />
      {/* Numeric value, positioned inside the arc opening */}
      <text x={cx} y={cy - 6} textAnchor="middle" fill={colour}
        fontSize={12} fontFamily="JetBrains Mono, monospace" fontWeight="bold">
        {spi.toFixed(3)}
      </text>
      <text x={cx} y={cy + 10} textAnchor="middle" fill="#64748b" fontSize={7}
        fontFamily="JetBrains Mono, monospace">
        SPI
      </text>
    </svg>
  )
}

export function KpiDashboard() {
  const { kpis, machines, instanceConfig } = useSimStore()
  const [throughputHistory, setThroughputHistory] = useState<{ t: number; v: number }[]>([])

  useEffect(() => {
    if (kpis) {
      setThroughputHistory((prev) => {
        const next = [...prev, { t: Date.now(), v: kpis.throughput_per_hour }]
        return next.length > 60 ? next.slice(-60) : next
      })
    }
  }, [kpis?.throughput_per_hour])

  const machineList = instanceConfig?.machines ?? []
  const utilData = machineList.map((m) => ({
    name: m.id.replace(/^Machine/i, 'M').slice(0, 7),
    util: Math.round((machines[m.id]?.utilisation_pct ?? 0) * 10) / 10,
  }))

  return (
    <motion.div
      initial={{ x: 80, opacity: 0 }}
      animate={{ x: 0, opacity: 1 }}
      transition={{ delay: 0.3, type: 'spring', stiffness: 100 }}
      className="absolute top-14 right-0 bottom-36 w-64 bg-[#0d1117]/90 border-l border-[#30363d] backdrop-blur-sm overflow-y-auto p-3 flex flex-col gap-3 z-10"
    >
      {kpis ? (
        <>
          {/* SPI gauge — centrepiece */}
          <div className="bg-[#161b22]/80 border border-[#30363d] rounded-lg py-2">
            <SpiGauge spi={kpis.spi} />
          </div>

          {/* Stat cards grid */}
          <div className="grid grid-cols-2 gap-2">
            <StatCard
              label="Completed"
              value={`${kpis.n_completed}/${kpis.n_total}`}
              colour={kpis.n_completed === kpis.n_total ? 'text-emerald-400' : 'text-teal-400'}
            />
            <StatCard
              label="Late orders"
              value={String(kpis.n_late)}
              colour={kpis.n_late > 0 ? 'text-rose-400' : 'text-emerald-400'}
            />
            <StatCard
              label="On-Time %"
              value={`${kpis.on_time_pct.toFixed(1)}%`}
              colour={kpis.on_time_pct >= 80 ? 'text-emerald-400' : kpis.on_time_pct >= 60 ? 'text-amber-400' : 'text-rose-400'}
            />
            <StatCard
              label="Throughput/h"
              value={kpis.throughput_per_hour.toFixed(1)}
              colour="text-cyan-400"
            />
          </div>

          {/* Flow / tardiness row */}
          <div className="grid grid-cols-2 gap-2">
            <StatCard
              label="Mean flow"
              value={`${kpis.mean_flow.toFixed(0)}m`}
              colour="text-slate-300"
            />
            <StatCard
              label="Mean tardiness"
              value={`${(kpis.mean_tardiness ?? 0).toFixed(0)}m`}
              colour={(kpis.mean_tardiness ?? 0) > 0 ? 'text-rose-300' : 'text-emerald-400'}
            />
          </div>

          {/* Throughput sparkline */}
          <div>
            <div className="text-xs text-slate-400 mb-1">Throughput / hr</div>
            <ResponsiveContainer width="100%" height={52}>
              <AreaChart data={throughputHistory}>
                <defs>
                  <linearGradient id="tealGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%"  stopColor="#2dd4bf" stopOpacity={0.35} />
                    <stop offset="95%" stopColor="#2dd4bf" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <Area type="monotone" dataKey="v" stroke="#2dd4bf" fill="url(#tealGrad)" strokeWidth={1.5} dot={false} />
              </AreaChart>
            </ResponsiveContainer>
          </div>

          {/* Utilisation bars */}
          {utilData.length > 0 && (
            <div>
              <div className="text-xs text-slate-400 mb-1">Utilisation %</div>
              <ResponsiveContainer width="100%" height={Math.max(50, utilData.length * 22)}>
                <BarChart data={utilData} layout="vertical" margin={{ left: 0, right: 8 }}>
                  <XAxis type="number" domain={[0, 100]} hide />
                  <YAxis type="category" dataKey="name" width={48} tick={{ fontSize: 9, fill: '#94a3b8' }} />
                  <Tooltip
                    contentStyle={{ background: '#161b22', border: '1px solid #30363d', fontSize: 11 }}
                    formatter={(v: number) => [`${v.toFixed(1)}%`, 'Util']}
                  />
                  <Bar dataKey="util" radius={2}
                    fill="#2dd4bf"
                    background={{ fill: '#21262d', radius: 2 }}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </>
      ) : (
        <div className="text-slate-500 text-xs text-center mt-8">Waiting for data…</div>
      )}
    </motion.div>
  )
}
